import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import LanguageToggle from './LanguageToggle';

const HeaderContainer = styled.header`
  background: rgba(0, 0, 0, 0.9);
  padding: 1rem 2rem;
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(10px);
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  img {
    width: auto;
    height: 40px;
  }

  h1 {
    margin: 0;
    color: #efb348;
    font-weight: 600;
    font-size: 1.5rem;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    flex-direction: column;
    gap: 2rem;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100vh;
    background: rgba(0, 0, 0, 0.95);
    transform: ${props =>
      props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    backdrop-filter: blur(10px);
    transition: transform 0.3s ease-in-out;
  }
`;

const MobileNavLinks = styled.div`
  display: none;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100vh;
    background: rgba(0, 0, 0, 0.85);
    transform: ${props =>
      props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    backdrop-filter: blur(10px);
    transition: transform 0.3s ease-in-out;
  }
`;

const HamburgerButton = styled.button`
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 101;

  span {
    width: 100%;
    height: 3px;
    background: #efb348;
    border-radius: 2px;
    transform-origin: center;
    transition: all 0.3s ease;
  }

  &.open span:nth-child(1) {
    transform: rotate(45deg) translate(7px, 7px);
  }

  &.open span:nth-child(2) {
    opacity: 0;
  }

  &.open span:nth-child(3) {
    transform: rotate(-45deg) translate(7px, -7px);
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileRightSection = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    gap: 1rem;
    align-items: center;
  }
`;

const DesktopRightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileLanguageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1rem;
  padding-top: 1rem;
`;

const StyledNavLink = styled(NavLink)`
  color: ${props => (props.isActive ? '#efb348' : 'white')};
  text-decoration: none;
  font-weight: ${props => (props.isActive ? '600' : '400')};
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    color: #efb348;
    background: rgba(239, 179, 72, 0.1);
  }

  @media (max-width: 768px) {
    padding: 1rem 2rem;
    font-size: 1.2rem;
    text-align: center;

    &:hover {
      background: rgba(239, 179, 72, 0.2);
    }
  }
`;

const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <HeaderContainer>
      <Nav>
        <Logo>
          <img src="/icons/logo.svg" alt="BoltUnion" />
          <h1>TUnion</h1>
        </Logo>

        {/* Desktop Navigation */}
        <DesktopRightSection>
          <NavLinks>
            <StyledNavLink to="/" end>
              {t('header.home')}
            </StyledNavLink>
            <StyledNavLink to="/about">{t('header.about')}</StyledNavLink>
            <StyledNavLink to="/contact">{t('header.contact')}</StyledNavLink>
            <StyledNavLink to="/otc">{t('header.otc')}</StyledNavLink>
            <StyledNavLink to="/CryptoTokenIntroduction">
              {t('header.CryptoTokenIntroduction')}
            </StyledNavLink>
          </NavLinks>
          <LanguageToggle />
        </DesktopRightSection>

        {/* Mobile Navigation - Only hamburger button */}
        <MobileRightSection>
          <HamburgerButton
            className={isMenuOpen ? 'open' : ''}
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </HamburgerButton>
        </MobileRightSection>

        {/* Mobile Menu */}
        <MobileNavLinks isOpen={isMenuOpen}>
          <StyledNavLink to="/" end onClick={closeMenu}>
            {t('header.home')}
          </StyledNavLink>
          <StyledNavLink to="/about" onClick={closeMenu}>
            {t('header.about')}
          </StyledNavLink>
          <StyledNavLink to="/contact" onClick={closeMenu}>
            {t('header.contact')}
          </StyledNavLink>
          <StyledNavLink to="/otc" onClick={closeMenu}>
            {t('header.otc')}
          </StyledNavLink>
          <StyledNavLink to="/CryptoTokenIntroduction" onClick={closeMenu}>
            {t('header.CryptoTokenIntroduction')}
          </StyledNavLink>

          {/* Language Toggle in Mobile Menu */}
          <MobileLanguageWrapper>
            <LanguageToggle />
          </MobileLanguageWrapper>
        </MobileNavLinks>
      </Nav>
    </HeaderContainer>
  );
};

export default Header;
